import { useState, useCallback, useEffect, useRef } from 'react';
import {
  loadCampaignViews as loadFromLocalStorage,
  saveCampaignViews as saveToLocalStorage,
  createCustomView,
  createDefaultCampaignViews,
  duplicateView as duplicateViewUtil,
  VIEW_TYPE_LABELS,
  relabelStockViewNames,
  normalizeCampaignViews,
  withSavedFieldVisibility,
} from '../utils/campaignViews/index';
import { readStoredColumnVisibility } from '../TableView/utils/columnVisibilityStorage';

function prepareCampaignViews(views, localSprint) {
  return normalizeCampaignViews(relabelStockViewNames(views), {
    cardDisplaySettings: localSprint?.cardDisplaySettings,
    storedColumnVisibility: readStoredColumnVisibility(localSprint?.id),
  });
}

/** A view added a moment ago can be in local storage before the campaign record has it. */
function withLocallySavedViews(views, campaignId) {
  const stored = loadFromLocalStorage(campaignId);
  if (!stored.length) return Array.isArray(views) ? views : [];
  if (!Array.isArray(views) || views.length === 0) return stored;
  const ids = new Set(views.map((view) => view?.id).filter(Boolean));
  const extras = stored.filter((view) => view?.id && !ids.has(view.id));
  return extras.length ? [...views, ...extras] : views;
}

/**
 * useCampaignViews Hook
 * 
 * Manages campaign views stored in the campaign data (backend).
 * 
 * UNIFIED VIEW MODEL:
 * All views are stored in sprint.views array. There's no distinction between
 * "default" and "custom" views anymore. When a campaign has no views:
 * - Regular campaigns get 5 initial views auto-created (Flow, Kanban, Table, Gantt, Todo)
 * - Multi-campaign views should already have their view from creation
 * 
 * Any view can be deleted as long as at least one view remains.
 */
export function useCampaignViews(campaignId, localSprint, setLocalSprint, isMultiCampaignMode = false, multiViewMode = 'flow', onViewTypeChange = null) {
  const [customViews, setCustomViews] = useState([]);
  const [activeCustomViewId, setActiveCustomViewId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  // Which campaign the current customViews belong to. Stays empty during a switch
  // so we don't restore a view against the previous campaign's list.
  const [viewsCampaignId, setViewsCampaignId] = useState(null);
  const hasMigratedRef = useRef(new Set()); // Track which campaigns we've migrated
  const lastCampaignIdRef = useRef(null);
  // Use ref for callback to avoid effect re-runs
  const onViewTypeChangeRef = useRef(onViewTypeChange);
  onViewTypeChangeRef.current = onViewTypeChange;

  // Load views when campaign changes (only on campaign switch, not on every localSprint change)
  useEffect(() => {
    if (!campaignId || !localSprint) {
      setCustomViews([]);
      setActiveCustomViewId(null);
      setIsInitialized(false);
      setViewsCampaignId(null);
      lastCampaignIdRef.current = null;
      return;
    }

    // Only initialize once per campaign
    if (lastCampaignIdRef.current === campaignId && isInitialized) {
      return;
    }

    lastCampaignIdRef.current = campaignId;
    setActiveCustomViewId(null);

    // Check if sprint has views stored in backend. Keep any view that was saved
    // locally and has not reached the campaign record yet (a quick tab switch).
    const rawViews = Array.isArray(localSprint.views) ? localSprint.views : [];
    const backendViews = withLocallySavedViews(rawViews, campaignId);
    // Get the last active view ID from the sprint data
    const lastActiveViewId = localSprint.lastActiveViewId;
    
    if (Array.isArray(backendViews) && backendViews.length > 0) {
      const views = prepareCampaignViews(backendViews, localSprint);
      setCustomViews(views);
      const rawIds = rawViews.map((view) => view?.id).join(',');
      const nextIds = views.map((view) => view?.id).join(',');
      if ((views !== backendViews || nextIds !== rawIds) && setLocalSprint) {
        setLocalSprint((prev) => {
          if (!prev || prev.id !== campaignId) return prev;
          return { ...prev, views };
        });
      }
      
      // DON'T auto-select a view here - let useInitialNavigation handle it
      // based on the URL's customViewId. This prevents race conditions where
      // we select the first view but the URL specifies a different one.
      // If no view is selected by useInitialNavigation, selectViewByType will
      // select the first matching view.
      setIsInitialized(true);
      setViewsCampaignId(campaignId);
    } else {
      // No backend views yet - check localStorage for migration
      const localStorageViews = loadFromLocalStorage(campaignId);
      
      if (localStorageViews.length > 0 && !hasMigratedRef.current.has(campaignId)) {
        // Migrate localStorage views to backend
        console.log(`[useCampaignViews] Migrating ${localStorageViews.length} views from localStorage to backend for campaign ${campaignId}`);
        hasMigratedRef.current.add(campaignId);
        
        const views = prepareCampaignViews(localStorageViews, localSprint);
        setCustomViews(views);
        // DON'T auto-select - let useInitialNavigation handle it
        setIsInitialized(true);
        setViewsCampaignId(campaignId);
        
        // Save to backend via localSprint update
        if (setLocalSprint) {
          setLocalSprint(prev => {
            if (!prev || prev.id !== campaignId) return prev;
            return { ...prev, views };
          });
        }
      } else {
        // No views anywhere - create defaults for regular campaigns
        if (!isMultiCampaignMode) {
          console.log(`[useCampaignViews] No views found, creating 6 default views`);
          const defaultViews = withSavedFieldVisibility(createDefaultCampaignViews(), {
            cardDisplaySettings: localSprint?.cardDisplaySettings,
            storedColumnVisibility: readStoredColumnVisibility(localSprint?.id),
          });
          
          setCustomViews(defaultViews);
          // DON'T auto-select - let useInitialNavigation handle it
          setIsInitialized(true);
          setViewsCampaignId(campaignId);
          
          // Save to backend
          if (setLocalSprint) {
            setLocalSprint(prev => {
              if (!prev || prev.id !== campaignId) return prev;
              return { ...prev, views: defaultViews };
            });
          }
        } else {
          // Multi-campaign mode - create a single view based on the saved view's viewMode
          console.log(`[useCampaignViews] Multi-campaign mode, creating single ${multiViewMode} view`);
          const [singleView] = withSavedFieldVisibility(
            [createCustomView(multiViewMode, VIEW_TYPE_LABELS[multiViewMode])],
            {
              cardDisplaySettings: localSprint?.cardDisplaySettings,
              storedColumnVisibility: readStoredColumnVisibility(campaignId),
            },
          );
          setCustomViews([singleView]);
          setActiveCustomViewId(singleView.id);
          setIsInitialized(true);
          setViewsCampaignId(campaignId);
          
          // Sync the view type with the toolbar
          if (onViewTypeChangeRef.current) {
            onViewTypeChangeRef.current(multiViewMode);
          }
        }
      }
    }
  }, [campaignId, localSprint?.id, isMultiCampaignMode]); // Only depend on IDs, not the full objects

  // Sync local state when backend views change (e.g., after save completes and data reloads)
  useEffect(() => {
    if (!isInitialized || !localSprint?.views) return;
    
    // If backend has views and they differ from local state, sync them
    // This handles the case where the sprint is reloaded from backend
    const backendViews = withLocallySavedViews(localSprint.views, campaignId);
    if (Array.isArray(backendViews)) {
      // Only update if the arrays are actually different (by comparing IDs)
      const localIds = customViews.map(v => v.id).sort().join(',');
      const backendIds = backendViews.map(v => v.id).sort().join(',');
      
      if (localIds !== backendIds) {
        console.log(`[useCampaignViews] Syncing views from backend (IDs changed)`);
        const views = prepareCampaignViews(backendViews, localSprint);
        setCustomViews(views);
        if (views !== backendViews && setLocalSprint) {
          setLocalSprint((prev) => {
            if (!prev || prev.id !== campaignId) return prev;
            return { ...prev, views };
          });
        }
      }
    }
  }, [localSprint?.views, isInitialized]);

  // Sync views to backend when they change
  const syncViewsToBackend = useCallback((updatedViews) => {
    if (!campaignId || !setLocalSprint) return;
    
    console.log(`[useCampaignViews] Syncing ${updatedViews.length} views to backend`);
    
    setLocalSprint(prev => {
      if (!prev || prev.id !== campaignId) return prev;
      return { ...prev, views: updatedViews };
    });
    
    // Also save to localStorage as backup
    saveToLocalStorage(campaignId, updatedViews);
  }, [campaignId, setLocalSprint]);

  // Get the currently active custom view (if any)
  const activeCustomView = activeCustomViewId
    ? customViews.find(v => v.id === activeCustomViewId)
    : null;

  // Add a new custom view
  const addView = useCallback((type, name) => {
    if (!campaignId) return null;
    
    const newView = createCustomView(type, name);
    const updatedViews = [...customViews, newView];
    setCustomViews(updatedViews);
    syncViewsToBackend(updatedViews);
    
    console.log(`[useCampaignViews] Added new view: ${newView.name} (${newView.id})`);
    
    // Automatically switch to the new view
    setActiveCustomViewId(newView.id);
    
    return newView;
  }, [campaignId, customViews, syncViewsToBackend]);

  // Update a custom view's settings
  const updateView = useCallback((viewId, updates) => {
    if (!campaignId) return;
    
    const updatedViews = customViews.map(v =>
      v.id === viewId
        ? { ...v, ...updates, updatedAt: new Date().toISOString() }
        : v
    );
    setCustomViews(updatedViews);
    syncViewsToBackend(updatedViews);
  }, [campaignId, customViews, syncViewsToBackend]);

  // Rename a custom view
  const renameView = useCallback((viewId, newName) => {
    updateView(viewId, { name: newName });
  }, [updateView]);

  // Duplicate a custom view
  const duplicateView = useCallback((viewId) => {
    if (!campaignId) return null;
    
    const viewToDuplicate = customViews.find(v => v.id === viewId);
    if (!viewToDuplicate) return null;
    
    const newView = duplicateViewUtil(viewToDuplicate);
    const updatedViews = [...customViews, newView];
    setCustomViews(updatedViews);
    syncViewsToBackend(updatedViews);
    
    return newView;
  }, [campaignId, customViews, syncViewsToBackend]);

  // Delete a view (must keep at least one view)
  // Returns the new active view (if the deleted view was active) so caller can update view type
  const deleteView = useCallback((viewId) => {
    if (!campaignId) return null;
    
    // Must keep at least one view
    if (customViews.length <= 1) {
      console.warn(`[useCampaignViews] Cannot delete last view`);
      return null;
    }
    
    // Find the index of the view being deleted
    const deletedIndex = customViews.findIndex(v => v.id === viewId);
    const updatedViews = customViews.filter(v => v.id !== viewId);
    setCustomViews(updatedViews);
    syncViewsToBackend(updatedViews);
    
    // If we deleted the active view, switch to the view to the left (or right if none on left)
    if (activeCustomViewId === viewId) {
      // Prefer the view to the left (deletedIndex - 1), otherwise take the view at the same index (which is now the right neighbor)
      const newIndex = deletedIndex > 0 ? deletedIndex - 1 : 0;
      const newActiveView = updatedViews[newIndex];
      setActiveCustomViewId(newActiveView?.id || null);
      return newActiveView; // Return so caller can update view type
    }
    return null;
  }, [campaignId, customViews, activeCustomViewId, syncViewsToBackend]);

  // Select a custom view (or null to go back to defaults)
  // Note: We no longer persist lastActiveViewId on every view switch because
  // it triggers a full sprint save which is slow for large campaigns.
  // The tab's customViewId handles restoring the correct view on navigation.
  const selectView = useCallback((viewId) => {
    setActiveCustomViewId(viewId);
  }, []);

  // Select the first view matching a given type (for history navigation)
  // Returns the selected view or null if no matching view found
  const selectViewByType = useCallback((viewType) => {
    const matchingView = customViews.find(v => v.type === viewType);
    if (matchingView) {
      selectView(matchingView.id);
      return matchingView;
    }
    return null;
  }, [customViews, selectView]);

  // Clear active view selection (will show first view)
  const clearActiveView = useCallback(() => {
    setActiveCustomViewId(null);
  }, []);

  // Reorder custom views
  const reorderViews = useCallback((newOrder) => {
    if (!campaignId) return;
    
    const viewMap = new Map(customViews.map(v => [v.id, v]));
    const reorderedViews = newOrder
      .map(id => viewMap.get(id))
      .filter(Boolean);
    
    setCustomViews(reorderedViews);
    syncViewsToBackend(reorderedViews);
  }, [campaignId, customViews, syncViewsToBackend]);

  // Update settings for the active custom view
  const updateActiveViewSettings = useCallback((settingKey, value) => {
    if (!activeCustomViewId) return;
    
    const view = customViews.find(v => v.id === activeCustomViewId);
    if (!view) return;
    
    const updatedSettings = {
      ...view.settings,
      [settingKey]: value,
    };
    
    updateView(activeCustomViewId, { settings: updatedSettings });
  }, [activeCustomViewId, customViews, updateView]);

  // Get settings for the active custom view (or null if no custom view active)
  const getActiveViewSettings = useCallback(() => {
    if (!activeCustomView) return null;
    return activeCustomView.settings;
  }, [activeCustomView]);

  return {
    customViews,
    activeCustomViewId,
    activeCustomView,
    viewsReady: viewsCampaignId === campaignId && customViews.length > 0,
    addView,
    updateView,
    renameView,
    duplicateView,
    deleteView,
    selectView,
    selectViewByType,
    clearActiveView,
    reorderViews,
    updateActiveViewSettings,
    getActiveViewSettings,
  };
}

export default useCampaignViews;
